import type { ReactNode } from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';
import {
    ConfirmContext,
    type ChoiceOption,
    type ChoiceOptions,
    type ConfirmContextValue,
    type ConfirmOptions,
} from './ConfirmModalContext';
import { ConfirmModal } from './ConfirmModal';

interface ConfirmModalProviderProps {
    children: ReactNode;
}

interface ConfirmRequestState {
    isOpen: boolean;
    title: string;
    description?: string;
    confirmLabel: string;
    cancelLabel: string;
    choices?: readonly ChoiceOption[];
}

const DEFAULT_CONFIRM_LABEL = 'Yes';
const DEFAULT_CANCEL_LABEL = 'No';
const DEFAULT_CHOICE_CANCEL_LABEL = 'Cancel';

/** Provider that exposes a promise-based confirmation API to UI workflows. */
export function ConfirmModalProvider({ children }: ConfirmModalProviderProps): JSX.Element {
    const [requestState, setRequestState] = useState<ConfirmRequestState | null>(null);
    const pendingResolverRef = useRef<((result: string | null) => void) | null>(null);

    const closeWithValue = useCallback((result: string | null): void => {
        if (pendingResolverRef.current !== null) {
            pendingResolverRef.current(result);
            pendingResolverRef.current = null;
        }

        setRequestState(null);
    }, []);

    /** Shows a prompt; one still open is answered as cancelled first, so its caller doesn't wait forever. */
    const open = useCallback((request: ConfirmRequestState, resolve: (result: string | null) => void): void => {
        pendingResolverRef.current?.(null);
        pendingResolverRef.current = resolve;
        setRequestState(request);
    }, []);

    const confirm = useCallback((options: ConfirmOptions): Promise<boolean> => {
        return new Promise<boolean>((resolve) => {
            open({
                isOpen: true,
                title: options.title,
                description: options.description,
                confirmLabel: options.confirmLabel ?? DEFAULT_CONFIRM_LABEL,
                cancelLabel: options.cancelLabel ?? DEFAULT_CANCEL_LABEL,
            }, (result) => { resolve(result !== null); });
        });
    }, [open]);

    const choose = useCallback((options: ChoiceOptions): Promise<string | null> => {
        return new Promise<string | null>((resolve) => {
            open({
                isOpen: true,
                title: options.title,
                description: options.description,
                confirmLabel: DEFAULT_CONFIRM_LABEL,
                cancelLabel: options.cancelLabel ?? DEFAULT_CHOICE_CANCEL_LABEL,
                choices: options.choices,
            }, resolve);
        });
    }, [open]);

    const contextValue: ConfirmContextValue = useMemo(() => ({ confirm, choose }), [confirm, choose]);

    return (
        <ConfirmContext.Provider value={contextValue}>
            {children}
            <ConfirmModal
                isOpen={requestState?.isOpen ?? false}
                title={requestState?.title ?? ''}
                description={requestState?.description}
                confirmLabel={requestState?.confirmLabel ?? DEFAULT_CONFIRM_LABEL}
                cancelLabel={requestState?.cancelLabel ?? DEFAULT_CANCEL_LABEL}
                choices={requestState?.choices}
                onConfirm={() => { closeWithValue('confirm'); }}
                onChoose={(id) => { closeWithValue(id); }}
                onCancel={() => { closeWithValue(null); }}
            />
        </ConfirmContext.Provider>
    );
}
